import React, { useState, useRef, useCallback } from 'react';
import { MapPin, Loader2, CheckCircle, AlertCircle, Truck, Navigation, Search } from 'lucide-react';
import axios from 'axios';

import { getBackendUrl } from '../utils/api';

const API = getBackendUrl();

/**
 * SimpleAddressForm - Formulário de Endereço Completo
 * 
 * Layout inspirado no design original com melhorias:
 * - GPS no topo
 * - Busca de endereço com autocomplete
 * - CEP opcional (auto-preenche campos quando digitado)
 * - Todos os campos visíveis e editáveis
 * - Botão "Calcular taxa de entrega" chamativo
 */
const SimpleAddressForm = ({
  restaurantId,
  onAddressComplete,
  onDeliveryFeeCalculated,
  onAddressReset,
  initialAddress = {},
  primaryColor = '#3B82F6',
  // NOVO: Receber o estado do resultado do pai para evitar perda em re-render
  externalDeliveryResult = null,
  onDeliveryResultChange
}) => {
  // Estados do endereço
  const [cep, setCep] = useState(initialAddress?.cep || '');
  const [street, setStreet] = useState(initialAddress?.street || '');
  const [number, setNumber] = useState(initialAddress?.number || '');
  const [complement, setComplement] = useState(initialAddress?.complement || '');
  const [neighborhood, setNeighborhood] = useState(initialAddress?.neighborhood || '');
  const [city, setCity] = useState(initialAddress?.city || '');
  const [state, setState] = useState(initialAddress?.state || '');
  
  // Estados de busca
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Estados de UI
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [isLoadingGps, setIsLoadingGps] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  // Usar resultado externo se fornecido, senão estado interno
  const [internalDeliveryResult, setInternalDeliveryResult] = useState(null);
  const deliveryResult = externalDeliveryResult !== undefined ? externalDeliveryResult : internalDeliveryResult;
  const setDeliveryResult = onDeliveryResultChange || setInternalDeliveryResult;
  const [error, setError] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  
  // Refs
  const numberRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Formatar CEP: 00000-000
  const formatCep = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 8);
    if (numbers.length > 5) {
      return numbers.slice(0, 5) + '-' + numbers.slice(5);
    }
    return numbers;
  };

  // Buscar endereço pelo CEP (quando completo)
  const handleCepChange = async (e) => {
    const formatted = formatCep(e.target.value);
    setCep(formatted);
    setError(null);
    setDeliveryResult(null);
    
    const cleanCep = formatted.replace(/\D/g, '');
    
    // Só busca quando CEP estiver completo (8 dígitos)
    if (cleanCep.length !== 8) {
      return;
    }
    
    console.log('🚀 [CEP] Buscando endereço para:', cleanCep);
    setIsLoadingCep(true);
    
    try {
      const response = await axios.get(`${API}/api/address/cep/${cleanCep}`);
      
      if (response.data.success) {
        console.log('📬 [CEP] Dados recebidos:', response.data);
        // Auto-preencher campos
        setStreet(response.data.street || street);
        setNeighborhood(response.data.neighborhood || neighborhood);
        setCity(response.data.city || city);
        setState(response.data.state || state);
        // Focar no número se estiver vazio
        if (!number) {
          setTimeout(() => numberRef.current?.focus(), 100);
        }
      }
    } catch (err) {
      console.error('❌ [CEP] Erro:', err);
      // Não mostrar erro - CEP é opcional
    } finally {
      setIsLoadingCep(false);
    }
  };

  // Usar localização GPS
  const handleUseGps = async () => {
    if (!navigator.geolocation) {
      setGpsError('Seu navegador não suporta geolocalização');
      return;
    }
    
    setIsLoadingGps(true);
    setGpsError(null);
    setError(null);
    setDeliveryResult(null);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        console.log('📍 [GPS] Coordenadas:', latitude, longitude);
        
        try {
          // Reverse geocode via backend (Google Geocoding API)
          const response = await axios.get(
            `${API}/api/address/reverse-geocode?lat=${latitude}&lng=${longitude}`
          );
          
          if (response.data?.success && response.data?.components) {
            const comp = response.data.components;
            console.log('📍 [GPS] Endereço (Google):', comp);
            
            setStreet(comp.street || '');
            setNeighborhood(comp.neighborhood || '');
            setCity(comp.city || '');
            setState(comp.state || '');
            setCep(comp.cep?.replace(/\D/g, '') || '');
            
            // Focar no número
            setTimeout(() => numberRef.current?.focus(), 100);
          } else {
            setGpsError('Não foi possível obter o endereço. Preencha manualmente.');
          }
        } catch (err) {
          console.error('❌ [GPS] Erro no reverse geocode:', err);
          setGpsError('Não foi possível obter o endereço. Preencha manualmente.');
        } finally {
          setIsLoadingGps(false);
        }
      },
      (err) => {
        console.error('❌ [GPS] Erro:', err);
        setIsLoadingGps(false);
        if (err.code === 1) {
          setGpsError('Permissão de localização negada. Habilite nas configurações do navegador.');
        } else {
          setGpsError('Não foi possível obter sua localização. Tente novamente.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Buscar endereço por texto (autocomplete)
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    setError(null);
    
    // Debounce da busca
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    if (value.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    searchDebounceRef.current = setTimeout(async () => {
      setIsLoadingSearch(true);
      
      try {
        // Busca via backend (Google Places API)
        const response = await axios.get(
          `${API}/api/address/autocomplete?q=${encodeURIComponent(value)}&limit=5`
        );
        
        if (response.data?.suggestions?.length > 0) {
          setSuggestions(response.data.suggestions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error('❌ [Search] Erro:', err);
      } finally {
        setIsLoadingSearch(false);
      }
    }, 400);
  }, []);

  // Selecionar sugestão de endereço
  const handleSelectSuggestion = async (suggestion) => {
    console.log('📍 [Search] Selecionado:', suggestion);
    
    // Se tem place_id (Google), buscar detalhes completos
    if (suggestion.place_id) {
      try {
        setIsLoadingSearch(true);
        const response = await axios.get(`${API}/api/maps/place-details/${suggestion.place_id}`);
        
        if (response.data?.success) {
          const comp = response.data.components || {};
          setStreet(comp.street || '');
          setNumber(comp.number || '');
          setNeighborhood(comp.neighborhood || '');
          setCity(comp.city || '');
          setState(comp.state || '');
          setCep(comp.cep?.replace(/\D/g, '') || '');
        } else {
          // Fallback: usar os dados da sugestão
          const comp = suggestion.components || {};
          setStreet(comp.street || suggestion.main_text || '');
          setNeighborhood(comp.neighborhood || '');
          setCity(comp.city || '');
          setState(comp.state || '');
          setCep(comp.cep?.replace(/\D/g, '') || '');
        }
      } catch (err) {
        console.error('❌ [Search] Erro ao buscar detalhes:', err);
        // Fallback
        const comp = suggestion.components || {};
        setStreet(comp.street || suggestion.main_text || '');
        setNeighborhood(comp.neighborhood || '');
        setCity(comp.city || '');
        setState(comp.state || '');
        setCep(comp.cep?.replace(/\D/g, '') || '');
      } finally {
        setIsLoadingSearch(false);
      }
    } else {
      // Sem place_id - usar dados diretos
      const comp = suggestion.components || {};
      setStreet(comp.street || suggestion.main_text || '');
      setNeighborhood(comp.neighborhood || '');
      setCity(comp.city || '');
      setState(comp.state || '');
      setCep(comp.cep?.replace(/\D/g, '') || '');
    }
    
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setDeliveryResult(null);
    
    // Focar no número se estiver vazio
    if (!number) {
      setTimeout(() => numberRef.current?.focus(), 100);
    }
  };

  // Validar se pode calcular
  const canCalculate = () => {
    return street.trim() && number.trim() && city.trim();
  };

  // Calcular taxa de entrega
  const handleCalculate = async () => {
    if (!canCalculate()) {
      setError('Preencha a rua, número e cidade');
      return;
    }
    
    setIsCalculating(true);
    setError(null);
    setDeliveryResult(null);
    
    const payload = {
      store_id: restaurantId,
      cep: cep.replace(/\D/g, '') || null,
      street,
      number,
      neighborhood,
      city,
      state
    };
    
    console.log('🚀 [Delivery] Calculando:', payload);
    
    try {
      const response = await axios.post(`${API}/api/delivery/calculate`, payload);
      const result = response.data;
      
      if (result.success) {
        const info = {
          fee: result.delivery_fee,
          distance: result.distance_km,
          distanceText: result.distance_text,
          duration: result.duration_minutes,
          durationText: result.duration_text,
          available: result.delivery_available,
          withinRange: result.delivery_available,
          message: result.message
        };
        
        console.log('✅ [Delivery] Resultado completo:', JSON.stringify(info));
        console.log('✅ [Delivery] available =', result.delivery_available);
        
        // IMPORTANTE: Setar o resultado ANTES de notificar o pai
        // para evitar que o re-render do pai limpe o estado
        setDeliveryResult(info);
        
        if (!result.delivery_available) {
          setError(result.message || 'Endereço fora da área de entrega');
        }
        
        // Notificar componente pai DEPOIS de setar estado local
        // Usar setTimeout para garantir que o estado local seja renderizado primeiro
        setTimeout(() => {
          onDeliveryFeeCalculated?.(info);
          onAddressComplete?.({
            cep: cep.replace(/\D/g, ''),
            street,
            number,
            complement,
            neighborhood,
            city,
            state,
            lat: result.client_lat,
            lng: result.client_lng
          });
        }, 0);
      } else {
        setError(result.error || 'Erro ao calcular entrega');
      }
    } catch (err) {
      console.error('❌ [Delivery] Erro:', err);
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Limpar formulário
  const handleReset = () => {
    console.log('🧹 [Address] Limpando formulário');
    setCep('');
    setStreet('');
    setNumber('');
    setComplement('');
    setNeighborhood('');
    setCity('');
    setState('');
    setSearchQuery('');
    setSuggestions([]);
    setDeliveryResult(null);
    setError(null);
    setGpsError(null);
    onAddressReset?.();
  };

  return (
    <div className="space-y-4" ref={wrapperRef}>
      
      {/* ===== BOTÃO GPS ===== */}
      <button
        type="button"
        onClick={handleUseGps}
        disabled={isLoadingGps || deliveryResult !== null}
        className="w-full py-3.5 border-2 border-dashed rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ 
          borderColor: primaryColor,
          color: primaryColor,
          backgroundColor: `${primaryColor}08`
        }}
        data-testid="gps-button"
      >
        {isLoadingGps ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            Obtendo localização...
          </>
        ) : (
          <>
            <Navigation size={20} />
            Usar minha localização
          </>
        )}
      </button>
      
      {gpsError && (
        <p className="text-sm text-amber-600 text-center">{gpsError}</p>
      )}
      
      {/* ===== DIVISOR ===== */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 font-medium">ou busque pelo endereço</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
      
      {/* ===== BUSCA DE ENDEREÇO ===== */}
      <div className="relative">
        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
          <Search size={14} />
          Buscar Endereço
        </label>
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Digite seu endereço..."
            disabled={deliveryResult !== null}
            className="w-full bg-white border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-100"
            style={{ '--tw-ring-color': primaryColor, fontSize: '16px' }}
            data-testid="address-search-input"
          />
          <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          {isLoadingSearch && (
            <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
          )}
        </div>
        
        {/* Dropdown de sugestões */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.place_id || index}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-start gap-3 border-b border-gray-100 last:border-0"
              >
                <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-sm text-gray-900 font-medium block truncate">{suggestion.main_text || suggestion.display_name}</span>
                  {suggestion.secondary_text && (
                    <span className="text-xs text-gray-500 block truncate">{suggestion.secondary_text}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* ===== CAMPOS DE ENDEREÇO ===== */}
      <div className="space-y-3">
        
        {/* Rua / Avenida */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Rua / Avenida
          </label>
          <input
            type="text"
            value={street}
            onChange={(e) => {
              setStreet(e.target.value);
              setDeliveryResult(null);
            }}
            placeholder="Nome da rua"
            disabled={deliveryResult !== null}
            className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-100"
            style={{ '--tw-ring-color': primaryColor, fontSize: '16px' }}
            data-testid="street-input"
          />
        </div>
        
        {/* Número, Complemento e CEP */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Número <span className="text-red-500">*</span>
            </label>
            <input
              ref={numberRef}
              type="text"
              inputMode="numeric"
              value={number}
              onChange={(e) => {
                setNumber(e.target.value);
                setDeliveryResult(null);
              }}
              placeholder="123"
              disabled={deliveryResult !== null}
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-100"
              style={{ '--tw-ring-color': primaryColor, fontSize: '16px' }}
              data-testid="number-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Complemento
            </label>
            <input
              type="text"
              value={complement}
              onChange={(e) => setComplement(e.target.value)}
              placeholder="Apto..."
              disabled={deliveryResult !== null}
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-100"
              style={{ '--tw-ring-color': primaryColor, fontSize: '16px' }}
              data-testid="complement-input"
            />
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              CEP
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={cep}
              onChange={handleCepChange}
              placeholder="00000-000"
              maxLength={9}
              disabled={deliveryResult !== null}
              className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-100"
              style={{ '--tw-ring-color': primaryColor, fontSize: '16px' }}
              data-testid="cep-input"
            />
            {isLoadingCep && (
              <Loader2 size={16} className="absolute right-3 top-[38px] text-gray-400 animate-spin" />
            )}
          </div>
        </div>
        
        {/* Bairro */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Bairro
          </label>
          <input
            type="text"
            value={neighborhood}
            onChange={(e) => {
              setNeighborhood(e.target.value);
              setDeliveryResult(null);
            }}
            placeholder="Nome do bairro"
            disabled={deliveryResult !== null}
            className="w-full bg-white border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-100"
            style={{ '--tw-ring-color': primaryColor, fontSize: '16px' }}
            data-testid="neighborhood-input"
          />
        </div>
        
        {/* Cidade - Estado */}
        {(city || state) && (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
            <MapPin size={16} className="text-gray-400" />
            <span className="text-sm text-gray-700 font-medium">
              {city}{state ? ` - ${state}` : ''}
            </span>
          </div>
        )}
      </div>
      
      {/* ===== BOTÃO CALCULAR TAXA ===== */}
      {!deliveryResult && (
        <button
          type="button"
          onClick={handleCalculate}
          disabled={isCalculating || !canCalculate()}
          className="w-full py-4 rounded-xl font-semibold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.01] active:scale-[0.99]"
          style={{ 
            backgroundColor: primaryColor,
            boxShadow: canCalculate() ? `0 4px 14px 0 ${primaryColor}40` : 'none'
          }}
          data-testid="calculate-delivery-btn"
        >
          {isCalculating ? (
            <>
              <Loader2 size={22} className="animate-spin" />
              <span>Calculando...</span>
            </>
          ) : (
            <>
              <Truck size={22} />
              <span>Calcular taxa de entrega</span>
            </>
          )}
        </button>
      )}
      
      {/* ===== RESULTADO ===== */}
      {deliveryResult && (
        <div 
          className={`p-4 rounded-xl border-2 ${
            deliveryResult.available 
              ? 'bg-green-50 border-green-300' 
              : 'bg-red-50 border-red-300'
          }`}
          data-testid="delivery-result"
        >
          <div className="flex items-start gap-3">
            {deliveryResult.available ? (
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Truck size={24} className="text-green-600" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle size={24} className="text-red-600" />
              </div>
            )}
            <div className="flex-1">
              {deliveryResult.available ? (
                <>
                  <p className="font-bold text-green-800 text-xl">
                    R$ {deliveryResult.fee?.toFixed(2).replace('.', ',')}
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    {deliveryResult.distanceText} • {deliveryResult.durationText}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-bold text-red-800 text-lg">Fora da área de entrega</p>
                  <p className="text-sm text-red-700 mt-1">
                    {deliveryResult.message}
                  </p>
                </>
              )}
            </div>
          </div>
          
          <button
            type="button"
            onClick={handleReset}
            className="w-full mt-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Alterar endereço
          </button>
        </div>
      )}
      
      {/* ===== ERRO ===== */}
      {error && !deliveryResult && (
        <div className="p-3 bg-red-50 rounded-xl border border-red-200 flex items-center gap-2">
          <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
};

export default SimpleAddressForm;
